import axios from "axios";
import { Link } from "react-router-dom";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate, useLocation } from "react-router-dom";
import L from "leaflet";
import { MapContainer, Polygon, TileLayer, WMSTileLayer, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";

import {
  faArrowLeft,
  faEye,
  faImage,
} from "@fortawesome/free-solid-svg-icons";

const containerStyle = {
  width: "100%",
  height: "50vh",
};

const center = {
  lat: 28.641832439372656,
  lng: 77.22480777651072,
};

// Bhuvan WMS Layer URL
const bhuvanLayerUrl = 'https://bhuvan-ras1.nrsc.gov.in/tilecache/tilecache.py';

// Helper component to fit bounds
const FitBounds = ({ bounds }) => {
  const map = useMap();

  useEffect(() => {
    if (bounds && bounds.isValid()) {
      map.fitBounds(bounds, { padding: [20, 20] });
    }
  }, [bounds, map]);

  return null;
};

// Helper function to parse coordinate pairs
const parseCoordinatePair = (pair) => {
  if (!Array.isArray(pair) || pair.length < 2) {
    return null;
  }

  const first = Number(pair[0]);
  const second = Number(pair[1]);

  if (Number.isNaN(first) || Number.isNaN(second)) {
    return null;
  }

  // Check if coordinates need to be swapped (lat/lng format)
  if (Math.abs(first) > 90 && Math.abs(second) <= 90) {
    return [second, first];
  }

  return [first, second];
};

// Helper function to normalize polygon shape data
const normalizePolygonShape = (rawPolygonShape) => {
  if (!rawPolygonShape) {
    return [];
  }

  let parsed = rawPolygonShape;
  if (typeof rawPolygonShape === "string") {
    try {
      parsed = JSON.parse(rawPolygonShape);
    } catch (_) {
      return [];
    }
  }

  if (!Array.isArray(parsed) || parsed.length === 0) {
    return [];
  }

  // Handle array of arrays format
  if (Array.isArray(parsed[0])) {
    return parsed.map(parseCoordinatePair).filter(Boolean);
  }

  // Handle array of objects format
  return parsed
    .map((point) => {
      if (!point || typeof point !== "object") {
        return null;
      }

      const latValue = Number(
        point.lat ?? point.latitude ?? point.Latitude ?? point.y
      );
      const lngValue = Number(
        point.lng ?? point.lon ?? point.longitude ?? point.Longitude ?? point.x
      );

      if (Number.isNaN(latValue) || Number.isNaN(lngValue)) {
        return null;
      }

      return [latValue, lngValue];
    })
    .filter(Boolean);
};

// Custom WMSTileLayer component for Bhuvan
const BhuvanWMSTileLayer = ({ url, layers, ...props }) => {
  const map = useMap();
  
  useEffect(() => {
    const wmsLayer = L.tileLayer.wms(url, {
      layers: layers,
      format: 'image/png',
      transparent: true,
      attribution: 'Bhuvan NRSC',
      maxZoom: 18,
      version: '1.1.1',
      srs: 'EPSG:3857',
      Maxresolution: 156543.033928,
      styles: '',
      ...props
    }).addTo(map);

    return () => {
      map.removeLayer(wmsLayer);
    };
  }, [map, url, layers, props]);

  return null;
};

const RKVYFarmerList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialState = location.state; // initial data passed via router
  const [farmerDetails, setFarmerDetails] = useState(initialState || null);
  const [cropSurveyData, setCropSurveyData] = useState(null);
  const [componentName, setComponentName] = useState("");
  const [activeImage, setActiveImage] = useState(null);
  const [coconutName, setCoconutName] = useState("");

  // Determine schemeCodeModel from sessionStorage
  const schemeCodeModel = sessionStorage.getItem("schemeCodeModel");

  // Flag to ensure fetch is only done once.
  const [fetched, setFetched] = useState(false);

  // When the component mounts (or farmerDetails changes), fetch detailed data if needed
  useEffect(() => {
    if (farmerDetails && !fetched) {
      console.log("Initial farmer details:::::", farmerDetails.projectCode);
      setCropSurveyData(farmerDetails.farmerCode);
      console.log("Initial farmer details:11111111111111111", farmerDetails.farmerCode.projectCode);
      // For schemeCodeModel "15" or if it's not set, fetch RKVY details only once
      if (schemeCodeModel === "15" || !schemeCodeModel) {
        fetchDetailedDataRKVY(farmerDetails.farmerCode.projectCode);
        setFetched(true);
      }
    }
  }, [farmerDetails, schemeCodeModel, fetched]);

  async function fetchDetailedDataRKVY(value) {
    try {
      const result = await axios.post(
        `${
          process.env.NODE_ENV === "development"
            ? process.env.REACT_APP_DEV_MODE
            : process.env.REACT_APP_PRO_MODE
        }/api/RKVY/RKVYProject/RKVYProjectExtList`,
        {
          surveyUserCode: "0",
          mobileNo: "",
          schemeCode: schemeCodeModel,
          orgTypeCode: "0",
          projectCode: value,
          stateCode: "0",
          districtCode: "0",
          subDistrictCode: "0",
          blockCode: "0",
          villageCode: "0",
          surveyNo: "",
        }
      );
      const data = result.data.dataContent[0];
      console.log("datadatadatadata", data);
      // Update our local state with the fetched details
      setFarmerDetails(data);
      //setCropSurveyData(data);
    } catch (error) {
      console.error("Error fetching RKVY details:", error);
    }
  }

  // Process polygon paths for Leaflet
  const polygonPaths = useMemo(() => {
    const polygonData = cropSurveyData?.projectSurveyPolygonList || 
                        farmerDetails?.projectSurveyPolygonList || [];
    
    if (!Array.isArray(polygonData) || polygonData.length === 0) {
      return [];
    }

    const shapes = polygonData
      .map((item) => normalizePolygonShape(item?.polygonShape))
      .filter((shape) => shape.length > 0);

    return shapes;
  }, [cropSurveyData, farmerDetails]);

  // Calculate map bounds from polygon paths
  const mapBounds = useMemo(() => {
    if (polygonPaths.length === 0) {
      return null;
    }

    const allPoints = polygonPaths.flat();
    if (allPoints.length === 0) {
      return null;
    }

    return L.latLngBounds(allPoints);
  }, [polygonPaths]);

  // Calculate map center
  const mapCenterLatLng = useMemo(() => {
    if (mapBounds && mapBounds.isValid()) {
      const mapCenterValue = mapBounds.getCenter();
      return [mapCenterValue.lat, mapCenterValue.lng];
    }

    return [center.lat, center.lng];
  }, [mapBounds]);

  // Handle active image click for a modal
  function handleActiveClick(index) {
    setActiveImage(index);
    console.log("Active image index:", index);
    if (cropSurveyData && cropSurveyData.projectRKVYImageLists) {
      const imagePath = cropSurveyData.projectRKVYImageLists[index].attPath;
      const innerHTML = `<img src="${imagePath}" alt="Project Image" class="img-fluid w-100 h-50" />`;
      document.getElementById("modalBody").innerHTML = innerHTML;
    }
  }

  return (
    <main id="main" className="main">
      <div className="pagetitle page_title_box d-flex align-items-center justify-content-between">
        <nav>
          <h1>Project Details View</h1>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">Project</li>
            <li className="breadcrumb-item active">Project Details View</li>
          </ol>
        </nav>
        <button
          type="button"
          className="btn btn-success"
          onClick={() => navigate(-1)}
        >
          <i>
            <FontAwesomeIcon icon={faArrowLeft} />
          </i>{" "}
          Back
        </button>
      </div>
      <section className="section">
        <div className="row">
          <div className="col-lg-12 dashboard">
            <div className="card">
              {cropSurveyData && (schemeCodeModel === "15" || !schemeCodeModel) && (
                <div className="card-body p-5 pt-2">
                  {/* Polygon Details with Bhuvan Map */}
                  <div className="row cropview">
                    <h5 className="formgrouping">Polygon Details</h5>
                    <MapContainer
                      center={mapCenterLatLng}
                      zoom={14}
                      maxZoom={18}
                      style={containerStyle}
                      scrollWheelZoom
                      zoomControl={true}
                    >
                      {/* Bhuvan WMS Base Layer */}
                      <BhuvanWMSTileLayer
                        url={bhuvanLayerUrl}
                        layers="bhuvan_wbase_ind"
                      />
                      
                      {/* Polygon Overlays */}
                      {polygonPaths.map((path, index) => (
                        <Polygon
                          key={`project-polygon-${index}`}
                          positions={path}
                          pathOptions={{
                            color: "#FF0000",
                            fillColor: "#FF0000",
                            fillOpacity: 0.1,
                            weight: 2,
                          }}
                          eventHandlers={{
                            mouseover: (e) => {
                              const layer = e.target;
                              layer.setStyle({
                                fillOpacity: 0.3,
                                weight: 3,
                              });
                            },
                            mouseout: (e) => {
                              const layer = e.target;
                              layer.setStyle({
                                fillOpacity: 0.1,
                                weight: 2,
                              });
                            },
                          }}
                        />
                      ))}
                      
                      {/* Auto-fit bounds */}
                      {mapBounds ? <FitBounds bounds={mapBounds} /> : null}
                    </MapContainer>
                  </div>
                  
                  {/* Survey Details */}
                  <h5 className="formgrouping">Survey Details</h5>
                  <div className="table-responsive mt-3 rkvy">
                    {/* Location-wise Table */}
                    <h5 className="mb-3">📍 Location Data</h5>
                    <table className="table table-bordered table-striped">
                      <thead>
                        <tr>
                          {cropSurveyData.stateName && <th>State</th>}
                          {cropSurveyData.districtName && <th>District</th>}
                          {cropSurveyData.subDistrictName && <th>Sub District</th>}
                          {cropSurveyData.villageName && <th>Village</th>}
                          {cropSurveyData.userName && <th>User Name</th>}
                          {cropSurveyData.mobileNo && <th>Mobile No</th>}
                          {cropSurveyData.farmerName && <th>Farmer Name</th>}
                          {cropSurveyData.farmerID && <th>Farmer ID</th>}
                          {cropSurveyData.surveyUserCode && <th>Survey No.</th>}
                          {cropSurveyData.surveyNumber && <th>Survey Number</th>}
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          {cropSurveyData.stateName && <td>{cropSurveyData.stateName}</td>}
                          {cropSurveyData.districtName && <td>{cropSurveyData.districtName}</td>}
                          {cropSurveyData.subDistrictName && <td>{cropSurveyData.subDistrictName}</td>}
                          {cropSurveyData.villageName && <td>{cropSurveyData.villageName}</td>}
                          {cropSurveyData.userName && <td>{cropSurveyData.userName}</td>}
                          {cropSurveyData.mobileNo && <td>{cropSurveyData.mobileNo}</td>}
                          {cropSurveyData.farmerName && <td>{cropSurveyData.farmerName}</td>}
                          {cropSurveyData.farmerID && <td>{cropSurveyData.farmerID}</td>}
                          {cropSurveyData.surveyUserCode && <td>{cropSurveyData.surveyUserCode}</td>}
                          {cropSurveyData.surveyNumber && <td>{cropSurveyData.surveyNumber}</td>}
                        </tr>
                      </tbody>
                    </table>

                    {/* Project-wise Table */}
                    <h5 className="mt-4 mb-3">📊 Project Data</h5>
                    <table className="table table-bordered table-striped">
                      <thead>
                        <tr>
                          {cropSurveyData.projectName && <th>Project Name</th>}
                          {cropSurveyData.agencyName && <th>Agency Name</th>}
                          {cropSurveyData.sectorName && <th>Sector Name</th>}
                          {cropSurveyData.subSectorName && <th>Sub Sector Name</th>}
                          {cropSurveyData.projectStatus && <th>Project Status</th>}
                          {cropSurveyData.numberofBeneficiary && <th>Number of Beneficiaries</th>}
                          {cropSurveyData.projectCost && <th>Project Cost</th>}
                          {cropSurveyData.projectApprovalDate && <th>Project Approval Date</th>}
                          {cropSurveyData.projectStartingDate && <th>Project Starting Date</th>}
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          {cropSurveyData.projectName && <td>{cropSurveyData.projectName}</td>}
                          {cropSurveyData.agencyName && <td>{cropSurveyData.agencyName}</td>}
                          {cropSurveyData.sectorName && <td>{cropSurveyData.sectorName}</td>}
                          {cropSurveyData.subSectorName && <td>{cropSurveyData.subSectorName}</td>}
                          {cropSurveyData.projectStatus && <td>{cropSurveyData.projectStatus}</td>}
                          {cropSurveyData.numberofBeneficiary && <td>{cropSurveyData.numberofBeneficiary}</td>}
                          {cropSurveyData.projectCost && <td>{cropSurveyData.projectCost}</td>}
                          {cropSurveyData.projectApprovalDate && <td>{cropSurveyData.projectApprovalDate}</td>}
                          {cropSurveyData.projectStartingDate && <td>{cropSurveyData.projectStartingDate}</td>}
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Farmer Plot Registration List */}
                  {cropSurveyData.farmerPlotRegistrationList &&
                    cropSurveyData.farmerPlotRegistrationList.length !== 0 && (
                      <>
                        <h5 className="formgrouping">Farmer Plot Registration List</h5>
                        <div className="row">
                          {cropSurveyData.farmerPlotRegistrationList.map(
                            (item, index) => (
                              <React.Fragment key={index}>
                                {item.farmerId && (
                                  <div className="col-md-3">
                                    <h6>Farmer Id</h6>
                                    <p>{item.farmerId}</p>
                                  </div>
                                )}
                                {item.plotId && (
                                  <div className="col-md-3">
                                    <h6>Plot Id</h6>
                                    <p>{item.plotId}</p>
                                  </div>
                                )}
                                {item.plotTotalArea && (
                                  <div className="col-md-3">
                                    <h6>Plot Total Area</h6>
                                    <p>{item.plotTotalArea}</p>
                                  </div>
                                )}
                                {item.plotatAreaCultivation && (
                                  <div className="col-md-3">
                                    <h6>Area Under Cultivation</h6>
                                    <p>{item.plotatAreaCultivation}</p>
                                  </div>
                                )}
                                {item.numofoilpalmtrees && (
                                  <div className="col-md-3">
                                    <h6>Number of Oil Palm Trees</h6>
                                    <p>{item.numofoilpalmtrees}</p>
                                  </div>
                                )}
                                {item.surveyNo && (
                                  <div className="col-md-3">
                                    <h6>Survey No.</h6>
                                    <p>{item.surveyNo}</p>
                                  </div>
                                )}
                                {item.plotCode && (
                                  <div className="col-md-3">
                                    <h6>Plot Code</h6>
                                    <p>{item.plotCode}</p>
                                  </div>
                                )}
                                {item.plantMonthYear && (
                                  <div className="col-md-3">
                                    <h6>Plant Month/Year</h6>
                                    <p>{item.plantMonthYear}</p>
                                  </div>
                                )}
                                {item.fbbProduction && (
                                  <div className="col-md-3">
                                    <h6>FBB Production</h6>
                                    <p>{item.fbbProduction}</p>
                                  </div>
                                )}
                                {item.agencyName && (
                                  <div className="col-md-3">
                                    <h6>Agency Name</h6>
                                    <p>{item.agencyName}</p>
                                  </div>
                                )}
                                {item.createdOn && (
                                  <div className="col-md-3">
                                    <h6>Created On</h6>
                                    <p>{item.createdOn.split("T")[0]}</p>
                                  </div>
                                )}
                              </React.Fragment>
                            )
                          )}
                        </div>
                      </>
                    )}
                  
                  {/* Project RKVY Image List */}
                  {cropSurveyData.projectRKVYImageLists &&
                    cropSurveyData.projectRKVYImageLists.length > 0 && (
                      <div className="row cropview">
                        <h5 className="formgrouping">Image Details</h5>
                        {console.log("cropSurveyData$", cropSurveyData)}
                        {cropSurveyData.projectRKVYImageLists.map((item, index) => (
                          <React.Fragment key={index}>
                            <div className="col-md-2">
                              <img
                                src={item.attPath}
                                alt="Project Image"
                                data-bs-toggle="modal"
                                data-bs-target="#staticBackdrop"
                                className="img-fluid imgdetials"
                                onClick={() => handleActiveClick(index)}
                              />
                            </div>
                            <div className="col-md-4">
                              <div className="row imgdetialsbg">
                                <div className="col-md-6">
                                  <h6>Latitude</h6>
                                  <p>{item.surveyorLat}</p>
                                </div>
                                <div className="col-md-6">
                                  <h6>Longitude</h6>
                                  <p>{item.surveyorLong}</p>
                                </div>
                              </div>
                            </div>
                          </React.Fragment>
                        ))}
                      </div>
                    )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default RKVYFarmerList;